import React, { useEffect, useMemo, useState } from 'react';
import '../styles/Roles.module.scss';
import { Checkbox } from "antd";
import { Icon } from "Components";
import { useTranslation } from 'react-i18next';

export type PermissionMode = 'view' | 'add' | 'edit' | 'clone';

export interface TextFieldProps {
  label: string;
  required?: boolean;
  placeholder?: string;
  value: string;
  disabled?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export interface CustomCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}

export interface PermissionSectionProps {
  section: PermissionSectionData;
  checkedMap: Record<string, boolean>;
  onToggleItem: (key: string, checked: boolean) => void;
  onToggleAll: (section: PermissionSectionData, checked: boolean) => void;
  isLast: boolean;
  readOnly?: boolean;
}

export interface SubGroupTitleProps {
  children: React.ReactElement | string;
}

export interface CheckboxRowProps {
  items: PermissionItem[];
  checkedMap: Record<string, boolean>;
  onToggle: (key: string, checked: boolean) => void;
  readOnly?: boolean;
}

export interface PermissionItem {
  key: string;
  label: string;
  checked?: boolean;
  disabled?: boolean;
}

export interface PermissionGroup {
  title?: string;
  items: PermissionItem[];
}

export interface PermissionSectionData {
  key: string;
  label: string;
  groups: PermissionGroup[];
}

export interface RoleFormValues {
  roleId: number;
  roleName?: string;
  isPrimary?: boolean;
  description?: string;
  permissions: any[]
  /** Only relevant for clone mode - the role being cloned from */
  sourceRoleName?: string;
}

export interface RoleSubmitPayload {
  roleName: string;
  description: string;
  permissions: number[];
}

export interface PermissionListProps {
  mode: PermissionMode;
  permissionList: PermissionSectionData[];
  initialValues?: RoleFormValues;
  loading?: boolean;
  onCancel?: () => void;
  setMode?: React.Dispatch<React.SetStateAction<'edit' | 'view'>>;
  onSubmit?: (values: RoleSubmitPayload) => void;
}

function RequiredAsterisk() {
  return <span className="cr-asterisk">*</span>;
}

function TextField({ label, required, placeholder, value, onChange, disabled }: TextFieldProps) {
  return (
    <div className="cr-input-wrap">
      <div className="cr-label-row">
        <span className="cr-label-text">{label}</span>
        {required && <RequiredAsterisk />}
      </div>
      <div className="cr-input">
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          className="cr-input-value"
        />
      </div>
    </div>
  );
}

function CustomCheckbox({ checked, disabled, onChange, label }: CustomCheckboxProps) {
  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      onChange={() => !disabled && onChange(!checked)}
    >
      {label}
    </Checkbox>
  );
}

function SubGroupTitle({ children }: SubGroupTitleProps) {
  return (
    <div className="cr-subgroup-title-wrap">
      <span className="cr-subgroup-title">{children}</span>
    </div>
  );
}

function CheckboxRow({ items, checkedMap, onToggle, readOnly }: CheckboxRowProps) {
  return (
    <div className="cr-checkbox-row">
      {items.map((item: PermissionItem) => (
        <div key={item.key} className="cr-checkbox-cell">
          <CustomCheckbox
            label={item.label}
            checked={checkedMap[item.key] ?? false}
            disabled={readOnly || item.disabled}
            onChange={(val: boolean) => onToggle(item.key, val)}
          />
        </div>
      ))}
    </div>
  );
}

function PermissionSection({ section, checkedMap, onToggleItem, onToggleAll, isLast, readOnly }: PermissionSectionProps) {
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const allItems: PermissionItem[] = section.groups.flatMap((g: PermissionGroup) => g.items);
  const selectableItems = allItems.filter((i) => !i.disabled);
  const allChecked = selectableItems.length > 0 && selectableItems.every((i) => checkedMap[i.key]);
  const someChecked = allItems.some((i) => checkedMap[i.key]);

  return (
    <div className={`cr-section ${!isLast ? 'cr-section-border' : ''}`}>
      <div className="cr-section-inner">
        <div className="cr-section-body">
          <div className="cr-section-header">
            <div className="cr-section-header-left"
              onClick={() => !readOnly && onToggleAll(section, !allChecked)}>
              <span
                className={`cr-checkbox-box ${allChecked ? 'cr-checkbox-box-checked' : someChecked ? 'cr-checkbox-box-partial' : ''
                  } ${readOnly ? 'cr-checkbox-disabled' : ''}`}
              >
                {(allChecked || someChecked) && <Checkbox checked={true} />}
              </span>
              <span className="cr-section-label">{section.label}</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="cr-collapse-btn"
              aria-label={open ? t('roles.permissionList.collapse') : t('roles.permissionList.expand')}
            >
              {open ? <Icon type="chevronUp" /> : <Icon type="chevronDown" />}
            </button>
          </div>

          {open && (
            <div className="cr-section-groups">
              {section.groups.map((group: PermissionGroup, gi: number) => (
                <div key={gi} className="cr-group">
                  {group.title && <SubGroupTitle>{group.title}</SubGroupTitle>}
                  <CheckboxRow
                    items={group.items}
                    checkedMap={checkedMap}
                    onToggle={onToggleItem}
                    readOnly={readOnly}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function buildInitialCheckedMap(permissionList: PermissionSectionData[],
  selectedPermissionIds: string[] = [],) {
  const selected = new Set(selectedPermissionIds.map(String));

  const map: Record<string, boolean> = {};

  permissionList.forEach(section =>
    section.groups.forEach(group =>
      group.items.forEach(item => {
        map[item.key] = selected?.has(String(item.key));
      }),
    ),
  );
  return map;
}

// Main component

function PermissionList({
  mode,
  setMode,
  permissionList,
  initialValues,
  loading,
  onCancel,
  onSubmit
}: PermissionListProps) {
  const { t } = useTranslation();

  const MODE_CONFIG: Record<
    PermissionMode,
    {
      heading: string;
      submitLabel: string;
      fieldsEditable: boolean;
      permissionsEditable: boolean;
      showFooter: boolean;
    }
  > = {
    view: {
      heading: t('roles.permissionList.heading.view'),
      submitLabel: t('roles.permissionList.buttons.close'),
      fieldsEditable: false,
      permissionsEditable: false,
      showFooter: true
    },
    add: {
      heading: t('roles.permissionList.heading.add'),
      submitLabel: t('roles.permissionList.buttons.addRole'),
      fieldsEditable: true,
      permissionsEditable: true,
      showFooter: true
    },
    edit: {
      heading: t('roles.permissionList.heading.edit'),
      submitLabel: t('roles.permissionList.buttons.saveChanges'),
      fieldsEditable: true,
      permissionsEditable: true,
      showFooter: true
    },
    clone: {
      heading: t('roles.permissionList.heading.clone'),
      submitLabel: t('roles.permissionList.buttons.editRole'),
      fieldsEditable: true,
      permissionsEditable: true,
      showFooter: true
    }
  };

  const config = MODE_CONFIG[mode];
  const readOnly = !config.permissionsEditable;
  const fieldsDisabled = !config.fieldsEditable || loading || initialValues?.isPrimary;

  const [roleName, setRoleName] = useState(initialValues?.roleName ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [checkedMap, setCheckedMap] =
    useState<Record<string, boolean>>(() =>
      buildInitialCheckedMap(
        permissionList,
        initialValues?.permissions,
      ),
    );
  const toggleItem = (key: string, val: boolean) => {
    if (readOnly) return;
    setCheckedMap((prev) => ({ ...prev, [key]: val }));
  };

  const toggleAllInSection = (section: PermissionSectionData, val: boolean) => {
    if (readOnly) return;
    setCheckedMap((prev) => {
      const next = { ...prev };
      section.groups.forEach((g) =>
        g.items.forEach((i) => {
          if (!i.disabled) next[i.key] = val;
        })
      );
      return next;
    });
  };

  const handleSubmit = () => {
    if (mode === 'view') {
      onCancel?.();
      return;
    }
    const permissions = Object.keys(checkedMap).filter((key) => checkedMap[key]).map(Number);
    onSubmit?.({ roleName, description, permissions });
  };

  const subheading = useMemo(() => {
    if (mode === 'clone' && initialValues?.sourceRoleName) {
      return (
        <p className="cr-subheading">
          {t('roles.permissionList.cloneMessage')} <span className="cr-subheading-strong">{initialValues.sourceRoleName}</span>.
        </p>
      );
    }
    return null;
  }, [mode, initialValues?.sourceRoleName, t]);

  useEffect(() => {
    setCheckedMap(
      buildInitialCheckedMap(
        permissionList,
        initialValues?.permissions,
      ),
    );
  }, [permissionList, initialValues?.permissions]);



  return (
    <div className="cr-page">
      <div className="cr-heading-row">
        <Icon type="chevronLeft" onClick={onCancel} />
        <h1 className="cr-heading">{config.heading}</h1>
      </div>
      {subheading}

      <div className="cr-outer-frame">
        <div className="cr-card">
          <div className="cr-fields-row">
            <TextField
              label={t('roles.permissionList.fields.roleName')}
              required
              placeholder={t('roles.permissionList.fields.insertName')}
              value={roleName}
              disabled={fieldsDisabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRoleName(e.target.value)}
            />
            <TextField
              label={t('roles.permissionList.fields.description')}
              required
              placeholder={t('roles.permissionList.fields.insertDescription')}
              value={description}
              disabled={fieldsDisabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            />
          </div>

          <div className="cr-permission-block">
            <div className="cr-permission-title-row">
              <span className="cr-permission-title">{t('roles.permissionList.permission')}</span>
            </div>

            <div className="cr-permission-panel">
              {permissionList.map((section, i) => (
                <PermissionSection
                  key={section.key}
                  section={section}
                  checkedMap={checkedMap}
                  onToggleItem={toggleItem}
                  onToggleAll={toggleAllInSection}
                  isLast={i === permissionList.length - 1}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        </div>

        {config.showFooter && (
          <div className="cr-footer-actions">
                <button
                  type="button"
                  className="cr-btn-ghost"
                  onClick={onCancel}
                >
                  {t('roles.permissionList.buttons.cancel')}
                </button>

              {mode === 'view' ? (
                <button
                  type="button"
                  className="cr-btn-primary"
                  onClick={() => setMode?.('edit')}
                >
                  {t('roles.permissionList.buttons.editRole')}
                </button>
              ) : (
                <button
                  type="button"
                  className="cr-btn-primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {config.submitLabel}
                </button>
              )}
            </div>
        )}
      </div>
    </div>
  );
}

export default PermissionList;